type ErrorWithData = {
  data?: unknown;
  message?: unknown;
};

function errorText(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "";
  }

  const candidate = error as ErrorWithData;
  if (candidate.data && typeof candidate.data === "object") {
    const code = (candidate.data as { code?: unknown }).code;
    if (typeof code === "string") {
      return code;
    }
  }

  return typeof candidate.message === "string" ? candidate.message : "";
}

export function getErrorCode(error: unknown): string | null {
  const text = errorText(error);
  const codeMatch = text.match(
    /(?:^|[\s{"'])code["']?\s*:\s*["']([A-Z_]+)["']/,
  );
  return codeMatch?.[1] ?? text.match(/^[A-Z][A-Z_]+$/)?.[0] ?? null;
}

export function isConnectionError(error: unknown): boolean {
  const text = errorText(error).toLowerCase();
  return ["network", "offline", "websocket", "fetch", "connection", "timed out"].some(
    (term) => text.includes(term),
  );
}

export function describeSignInError(error: unknown): string {
  const text = errorText(error).toLowerCase();

  if (isConnectionError(error)) {
    return "Could not reach Packful. Check your connection and try again.";
  }
  if (
    text.includes("verify code") ||
    text.includes("invalid") ||
    text.includes("expired") ||
    text.includes("credentials")
  ) {
    return "That code is invalid or expired. Request a new code and try again.";
  }
  return "Sign-in could not be completed. Try again or use another email address.";
}

export function describeOperatorError(
  error: unknown,
  action: "create" | "rename" | "resend" | "load",
): string {
  const code = getErrorCode(error);

  if (isConnectionError(error)) {
    return "Could not reach Packful. Check your connection and try again.";
  }
  if (code === "DUPLICATE_EMAIL") {
    return "That administrator email is already authorised for an organisation.";
  }
  if (code === "INVITATION_NOT_AVAILABLE") {
    return "That invitation is no longer available to resend. Refresh the list.";
  }
  if (code === "NOT_FOUND") {
    return "That organisation no longer exists. Refresh the list and try again.";
  }
  if (code === "VALIDATION_ERROR") {
    return "Check the entered details and try again.";
  }
  if (code === "FORBIDDEN" || code === "NOT_OPERATOR") {
    return "Your operator access is no longer available. Sign out and contact the Packful owner.";
  }

  const messages = {
    create: "The organisation could not be created. No changes were made.",
    rename: "The organisation could not be renamed. Try again.",
    resend: "The invitation could not be resent. Try again.",
    load: "Organisations could not be loaded. Try again.",
  };
  return messages[action];
}
